import { useState } from 'react';
import { exportProposalPDF } from './exportPDF';
import { showToast } from '@/components/Toast';
import { createRoot } from 'react-dom/client';
import PDFCover from './PDFCover';
import PDFVantagens from './PDFVantagens';
import PDFContent from './PDFContent';
import PDFBackCover from './PDFBackCover';
import { getCoverImageByCategory } from './PDFCover';
import { defaultPDFExportConfig } from '@/mocks/pdf-export';
import type { ProposalMock } from '@/mocks/proposals';
import type { TemplateConfig } from '@/mocks/template-config';

interface PDFExporterProps {
  proposal: ProposalMock;
  config: TemplateConfig;
}

export default function PDFExporter({ proposal, config }: PDFExporterProps) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    // Container fora da tela, visível (opacity: 1) para html2canvas capturar corretamente
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'fixed';
    tempContainer.style.top = '0';
    tempContainer.style.left = '-9999px';
    tempContainer.style.width = '210mm';
    tempContainer.style.opacity = '1';
    tempContainer.style.pointerEvents = 'none';
    tempContainer.style.zIndex = '-1';
    document.body.appendChild(tempContainer);

    try {
      const root = createRoot(tempContainer);

      const advantagesText = (proposal as unknown as Record<string, unknown>).advantages_text as string || '';
      const hasAdvantages = advantagesText.trim().length > 0;
      const coverImage = proposal.cover_image || getCoverImageByCategory(proposal.category || 'cameras');

      root.render(
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, width: '210mm' }}>
          <div id="pdf-page-cover">
            <PDFCover proposal={proposal} config={config} />
          </div>
          {hasAdvantages && (
            <div id="pdf-page-vantagens">
              <PDFVantagens proposal={proposal} config={config} coverImage={coverImage} />
            </div>
          )}
          <div id="pdf-page-content">
            <PDFContent proposal={proposal} config={config} />
          </div>
          <div id="pdf-page-backcover">
            <PDFBackCover config={config} category={proposal.category} />
          </div>
        </div>
      );

      // Wait for React render + images to load
      await new Promise((resolve) => setTimeout(resolve, 1800));

      const pdfExportConfig = {
        ...defaultPDFExportConfig,
        includePageNumbers: config.pdf?.includePageNumbers ?? false,
      };

      await exportProposalPDF(tempContainer, proposal, config, hasAdvantages, pdfExportConfig);
      showToast('PDF gerado com sucesso!', 'success');
      root.unmount();
    } catch {
      showToast('Erro ao gerar o PDF. Tente novamente.', 'error');
    } finally {
      if (tempContainer.parentNode) {
        document.body.removeChild(tempContainer);
      }
      setDownloading(false);
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      className="flex-1 bg-brand-card hover:bg-brand-card/80 border border-brand-gold/40 text-brand-gold font-semibold py-3 rounded-md flex items-center justify-center gap-2 transition-colors whitespace-nowrap disabled:opacity-50"
    >
      {downloading ? (
        <div className="w-5 h-5 border-2 border-brand-gold/30 border-t-brand-gold rounded-full animate-spin" />
      ) : (
        <i className="ri-download-line" />
      )}
      {downloading ? 'Gerando PDF...' : 'BAIXAR PDF'}
    </button>
  );
}